import React, { useEffect, useState } from "react";
import addressService from "../api/addressService";

const {
    getAddresses,
    getAddressById,
    createAddress,
    updateAddress,
    deleteAddress
} = addressService;

const emptyForm = {
    street: "",
    postalCodeId: "",
    latitude: "",
    longitude: ""
};

const AddressPage = () => {
    const [addresses, setAddresses] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getAddresses();
            setAddresses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Error loading addresses");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setForm(emptyForm);
        setEditId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const payload = {
            street: form.street.trim(),
            postalCodeId: form.postalCodeId ? Number(form.postalCodeId) : null,
            latitude: form.latitude === "" ? null : Number(form.latitude),
            longitude: form.longitude === "" ? null : Number(form.longitude)
        };

        if (!payload.street) {
            setError("Street is required");
            return;
        }

        if (!payload.postalCodeId) {
            setError("PostalCodeId is required");
            return;
        }

        try {
            if (editId) {
                await updateAddress(editId, payload);
                setSuccess("Address updated");
            } else {
                await createAddress(payload);
                setSuccess("Address created");
            }

            resetForm();
            await fetchAddresses();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.response?.data ||
                "Error saving address"
            );
        }
    };

    const handleEdit = async (id) => {
        setError("");
        setSuccess("");

        try {
            const data = await getAddressById(id);

            setForm({
                street: data.street ?? "",
                postalCodeId: data.postalCodeId ?? "",
                latitude: data.latitude ?? "",
                longitude: data.longitude ?? ""
            });

            setEditId(id);
        } catch (err) {
            console.error(err);
            setError("Error loading address");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this address?")) return;

        setError("");
        setSuccess("");

        try {
            await deleteAddress(id);
            setSuccess("Address deleted");

            if (editId === id) {
                resetForm();
            }

            await fetchAddresses();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.response?.data ||
                "Error deleting address"
            );
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Addresses</h2>

            <form onSubmit={handleSubmit} className="mb-6 grid gap-3 max-w-2xl">
                <input
                    type="text"
                    name="street"
                    placeholder="Street"
                    value={form.street}
                    onChange={handleChange}
                    className="border p-2 rounded"
                    required
                />

                <input
                    type="number"
                    name="postalCodeId"
                    placeholder="Postal Code ID"
                    value={form.postalCodeId}
                    onChange={handleChange}
                    className="border p-2 rounded"
                    required
                />

                <input
                    type="number"
                    step="any"
                    name="latitude"
                    placeholder="Latitude"
                    value={form.latitude}
                    onChange={handleChange}
                    className="border p-2 rounded"
                />

                <input
                    type="number"
                    step="any"
                    name="longitude"
                    placeholder="Longitude"
                    value={form.longitude}
                    onChange={handleChange}
                    className="border p-2 rounded"
                />

                <div className="flex gap-2">
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded"
                    >
                        {editId ? "Update" : "Add"}
                    </button>

                    {editId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="bg-gray-300 text-black px-4 py-2 rounded"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            {loading && <div>Loading...</div>}
            {error && <div className="text-red-500 mb-3">{String(error)}</div>}
            {success && <div className="text-green-600 mb-3">{success}</div>}

            <table className="min-w-full border">
                <thead>
                    <tr>
                        <th className="border px-4 py-2">ID</th>
                        <th className="border px-4 py-2">Street</th>
                        <th className="border px-4 py-2">PostalCodeId</th>
                        <th className="border px-4 py-2">Postal Code</th>
                        <th className="border px-4 py-2">City</th>
                        <th className="border px-4 py-2">Country</th>
                        <th className="border px-4 py-2">Latitude</th>
                        <th className="border px-4 py-2">Longitude</th>
                        <th className="border px-4 py-2">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {addresses.map((item) => (
                        <tr key={item.id}>
                            <td className="border px-4 py-2">{item.id}</td>
                            <td className="border px-4 py-2">{item.street}</td>
                            <td className="border px-4 py-2">{item.postalCodeId ?? "-"}</td>
                            <td className="border px-4 py-2">{item.postalCode ?? "-"}</td>
                            <td className="border px-4 py-2">{item.city ?? item.cityId ?? "-"}</td>
                            <td className="border px-4 py-2">{item.country ?? item.countryId ?? "-"}</td>
                            <td className="border px-4 py-2">{item.latitude ?? "-"}</td>
                            <td className="border px-4 py-2">{item.longitude ?? "-"}</td>
                            <td className="border px-4 py-2">
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleEdit(item.id)}
                                        className="bg-yellow-400 text-white px-2 py-1 rounded"
                                    >
                                        Edit
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(item.id)}
                                        className="bg-red-500 text-white px-2 py-1 rounded"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}

                    {addresses.length === 0 && (
                        <tr>
                            <td colSpan={9} className="text-center text-gray-500 py-4">
                                No addresses found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default AddressPage;